import { emptyStates } from "@features/dashboard/constants";
import PrintButton from "@features/report/components/print/PrintButton";
import PrintableReportContainer from "@features/report/components/print/PrintableReportContainer";
import { usePrinting } from "@features/report/hooks/usePrinting";
import { useAppStore } from "@hooks/useAppStore";
import { titleCase } from "@memorang/helpers/common";
import { isWeb } from "@memorang/helpers/platform";
import type { MixedItemTag } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { CustomAppBar } from "@memorang/ui/components/CustomAppbar";
import EmptyState from "@memorang/ui/components/EmptyView";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import TopicSortBottomSheet from "@memorang/ui/components/Topics/TopicSortBottomSheet";
import TopicViewMainContent from "@memorang/ui/components/Topics/TopicViewMainContent";
import TopicsFooter from "@memorang/ui/components/Topics/TopicsFooter";
import TopicsSortDialog from "@memorang/ui/components/Topics/TopicsSortDialog";
import { useDevice } from "@memorang/ui/useDevice";
import { router } from "expo-router";
import { useMemo } from "react";
import { Card } from "react-native-paper";
import { ProgressEmptySvg } from "../../../assets";
import useTopicsSort from "./useTopicsSort";

type Props = {
	tags: MixedItemTag[];
	fromReport?: boolean;
	type?: string;
};

const tagTypeMap: Record<string, string> = {
	"Diagnostic Level": "Reasoning",
	"Major Area": "Knowledge Levels",
};

const TopicsDetailView = ({ tags, fromReport = false, type }: Props) => {
	const { isMobile } = useDevice();
	const scoreRanges = useAppStore((state) => state.app?.scoreRanges);

	const filteredTags = useMemo(() => {
		return (
			tags.filter((tag) =>
				tagTypeMap[tag.type]
					? tagTypeMap[tag.type].toLowerCase() === type?.toLowerCase()
					: tag.type.toLowerCase() === type?.toLowerCase(),
			) || tags
		);
	}, [type, tags]);

	const {
		showSortModal,
		handleSortPress,
		handleSelectSortByType,
		data,
		handleHideSortModal,
		selectedSortByItem,
		localTags,
		sortOrderType,
		title,
		numTotalTopics,
		numTotalItems,
		numCorrectItems,
		numIncorrectItems,
		numSkippedItems,
		breadcrumbs,
	} = useTopicsSort(filteredTags);

	const { isPrintMode } = usePrinting();

	if (isPrintMode && isWeb && fromReport) {
		return <PrintableReportContainer />;
	}

	const headerTitle = type
		? `${titleCase(type)} performance`
		: "Topic performance";

	return (
		<>
			<CustomAppBar
				title={headerTitle}
				breadcrumbs={[
					...breadcrumbs,
					{
						label: headerTitle,
					},
				]}
				renderRight={() => <PrintButton />}
				onPressBack={() => {
					if (router.canGoBack()) {
						router.back();
					} else {
						router.push("/(protected)/(tabs)/progress");
					}
				}}
			/>
			<Box flex={1}>
				<ScrollContainer maxWidth="sm">
					{filteredTags.length ? (
						<>
							{isMobile ? (
								<Box justifyContent="space-between" height="100%" flexGrow={1}>
									<TopicViewMainContent
										title={title}
										onSortPress={handleSortPress}
										tags={localTags}
										selectedSortByItem={selectedSortByItem}
										selectedSortOrder={sortOrderType}
										scoreRanges={scoreRanges}
									/>
									<TopicsFooter
										numTotalTopics={numTotalTopics}
										numTotalItems={numTotalItems}
										numCorrectItems={numCorrectItems}
										numIncorrectItems={numIncorrectItems}
										numSkippedItems={numSkippedItems}
										scoreRanges={scoreRanges}
									/>
								</Box>
							) : (
								<Box padding={24}>
									<Card
										style={{
											justifyContent: "space-between",
											maxHeight: "100%",
										}}
									>
										<TopicViewMainContent
											onSortPress={handleSortPress}
											tags={localTags}
											selectedSortOrder={sortOrderType}
											title={title}
											selectedSortByItem={selectedSortByItem}
											scoreRanges={scoreRanges}
										/>
										<TopicsFooter
											numTotalTopics={numTotalTopics}
											numTotalItems={numTotalItems}
											numCorrectItems={numCorrectItems}
											numIncorrectItems={numIncorrectItems}
											numSkippedItems={numSkippedItems}
											scoreRanges={scoreRanges}
										/>
									</Card>
								</Box>
							)}
						</>
					) : (
						<EmptyState
							{...emptyStates.progress}
							cta={"Study now"}
							image={ProgressEmptySvg}
							handleAction={() => {
								router.push("/home");
							}}
						/>
					)}
				</ScrollContainer>
				{isMobile ? (
					<TopicSortBottomSheet
						showModal={showSortModal}
						handleSheetClose={handleHideSortModal}
						handleSortByChange={handleSelectSortByType}
						data={data}
						selectedSortByItem={selectedSortByItem}
						sortOrderType={sortOrderType}
					/>
				) : null}
				{isMobile ? null : (
					<TopicsSortDialog
						visible={showSortModal}
						handleClose={handleHideSortModal}
						handleSortByChange={handleSelectSortByType}
						data={data}
						selectedSortByItem={selectedSortByItem}
						sortOrderType={sortOrderType}
					/>
				)}
			</Box>
		</>
	);
};

export default TopicsDetailView;
